import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileSpreadsheet, Users, CheckCircle, AlertCircle, Download, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import api from '../lib/api';

const BulkEnrollment = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [schools, setSchools] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState('');
  const [selectedSchool, setSelectedSchool] = useState('');
  const [autoApproveCerts, setAutoApproveCerts] = useState(false);
  const [certTier, setCertTier] = useState('bronze');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [programsRes, schoolsRes] = await Promise.all([
        api.get('/programs'),
        api.get('/schools')
      ]);
      setPrograms(programsRes.data);
      setSchools(schoolsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validTypes = ['.csv', '.xlsx', '.xls'];
      const ext = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
      if (!validTypes.includes(ext)) {
        toast.error('Invalid file type. Please upload CSV or Excel file.');
        return;
      }
      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error('File too large. Maximum size is 10MB.');
        return;
      }
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }
    if (!selectedProgram) {
      toast.error('Please select a program');
      return;
    }

    setUploading(true);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('program_id', selectedProgram);
    if (selectedSchool && selectedSchool !== 'none') formData.append('school_id', selectedSchool);
    formData.append('auto_approve_certificates', autoApproveCerts);
    formData.append('certificate_tier', certTier);

    try {
      // Do NOT set Content-Type - let browser set multipart/form-data with boundary
      const response = await api.post('/bulk/register', formData);
      setResult(response.data);
      if (response.data.students_created > 0) {
        toast.success(`Successfully enrolled ${response.data.students_created} students!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const csv = 'student_name,student_email,student_dob,parent_name,parent_email,parent_phone\nJohn Doe,john@example.com,2015-05-15,Jane Doe,jane@example.com,+268 7612 3456\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk_enrollment_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="bulk-enrollment-page">
      <Button variant="ghost" onClick={() => navigate('/students')} className="mb-4">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to Students
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-6 h-6" />
            Bulk Student Enrollment
          </CardTitle>
          <CardDescription>
            Upload a CSV or Excel file to enroll multiple students at once
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Instructions */}
          <div className="bg-slate-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">File Requirements:</h4>
            <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
              <li>Format: CSV or Excel (.xlsx, .xls)</li>
              <li>Maximum size: 10MB</li>
              <li>Required columns: <code className="bg-slate-200 px-1 rounded">student_name</code></li>
              <li>Optional columns: <code className="bg-slate-200 px-1 rounded">student_email, student_dob, parent_name, parent_email, parent_phone</code></li>
            </ul>
            <Button variant="outline" size="sm" className="mt-3" onClick={downloadTemplate}>
              <Download className="w-4 h-4 mr-2" /> Download Template
            </Button>
          </div>

          {/* File Upload */}
          <div>
            <Label>Upload File</Label>
            <div className="mt-2 border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-teal-400 transition-colors">
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
                data-testid="file-input"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <FileSpreadsheet className="w-12 h-12 mx-auto text-slate-400 mb-2" />
                {file ? (
                  <p className="text-teal-600 font-medium">{file.name}</p>
                ) : (
                  <p className="text-slate-500">Click to select CSV or Excel file</p>
                )}
              </label>
            </div>
          </div>

          {/* Program Selection */}
          <div>
            <Label>Program *</Label>
            <Select value={selectedProgram} onValueChange={setSelectedProgram}>
              <SelectTrigger className="mt-1" data-testid="program-select">
                <SelectValue placeholder="Select program" />
              </SelectTrigger>
              <SelectContent>
                {programs.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* School Selection */}
          <div>
            <Label>Partner School (Optional)</Label>
            <Select value={selectedSchool} onValueChange={setSelectedSchool}>
              <SelectTrigger className="mt-1" data-testid="school-select">
                <SelectValue placeholder="Select school for co-branding" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {schools.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Certificate Options */}
          <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <Label>Auto-approve Certificates</Label>
                <p className="text-sm text-slate-500">Create pre-approved certificates for all students</p>
              </div>
              <Switch
                checked={autoApproveCerts}
                onCheckedChange={setAutoApproveCerts}
                data-testid="auto-approve-switch"
              />
            </div>

            {autoApproveCerts && (
              <div>
                <Label>Certificate Tier</Label>
                <Select value={certTier} onValueChange={setCertTier}>
                  <SelectTrigger className="mt-1" data-testid="tier-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gold">🥇 Gold</SelectItem>
                    <SelectItem value="silver">🥈 Silver</SelectItem>
                    <SelectItem value="bronze">🥉 Bronze</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Upload Button */}
          <Button
            onClick={handleUpload}
            disabled={!file || !selectedProgram || uploading}
            className="w-full"
            data-testid="upload-btn"
          >
            {uploading ? (
              <>Processing...</>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" /> Upload and Enroll Students
              </>
            )}
          </Button>

          {/* Results */}
          {result && (
            <div className={`p-4 rounded-lg ${!result.success ? 'bg-red-50' : result.errors?.length > 0 ? 'bg-amber-50' : 'bg-green-50'}`} data-testid="result-panel">
              <h4 className="font-semibold flex items-center gap-2 mb-3">
                {!result.success ? (
                  <><AlertCircle className="w-5 h-5 text-red-600" /> Upload Failed</>
                ) : result.errors?.length === 0 ? (
                  <><CheckCircle className="w-5 h-5 text-green-600" /> Upload Successful</>
                ) : (
                  <><AlertCircle className="w-5 h-5 text-amber-600" /> Upload Completed with Warnings</>
                )}
              </h4>
              {result.summary && (
                <p className="text-sm text-slate-600 mb-3">{result.summary}</p>
              )}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
                <div className="bg-white p-3 rounded">
                  <p className="text-2xl font-bold text-slate-800">{result.total_rows}</p>
                  <p className="text-xs text-slate-500">Total Rows</p>
                </div>
                <div className="bg-white p-3 rounded">
                  <p className="text-2xl font-bold text-green-600">{result.students_created}</p>
                  <p className="text-xs text-slate-500">Students Created</p>
                </div>
                <div className="bg-white p-3 rounded">
                  <p className="text-2xl font-bold text-blue-600">{result.parents_created}</p>
                  <p className="text-xs text-slate-500">Parents Created</p>
                </div>
                <div className="bg-white p-3 rounded">
                  <p className="text-2xl font-bold text-purple-600">{result.certificates_created}</p>
                  <p className="text-xs text-slate-500">Certificates</p>
                </div>
                <div className="bg-white p-3 rounded">
                  <p className="text-2xl font-bold text-orange-600">{result.duplicates_skipped || 0}</p>
                  <p className="text-xs text-slate-500">Duplicates Skipped</p>
                </div>
              </div>
              {result.errors?.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-amber-700 mb-2">Issues ({result.errors.length}):</p>
                  <ul className="text-sm text-amber-600 space-y-1 max-h-32 overflow-y-auto">
                    {result.errors.map((err, i) => (
                      <li key={i}>• {err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BulkEnrollment;
